import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { TableSettings } from 'components/TableSettings';
import type { ThreeForms } from 'components/TotalCount';
import { TotalCount } from 'components/TotalCount';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogUerBaseCostData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { ConstructionTypeCodeEnum } from 'types/constructionTypeCode';
import { Button, cnMixSpace, Grid, GridItem } from 'uikit';
import { destroyAgGridTableFilters, getCheckedIdsArr } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useCatalog } from '../CatalogList/hooks/useCatalog';
import { CatalogTablePage } from '../Common/CatalogTablePage';
import { useGlobalConstructionTypes } from '../Common/CreateOrEditOtherVariable/hooks/useGlobalConstructionTypes';
import { EstimateBasisModal } from '../Common/EstimateBasisModal/EstimateBasisModal';
import { MassRatioApplication } from '../Common/MassRatioApplication';
import { CreateOrEditEstimate } from '../CreateOrEditEstimate/CreateOrEditEstimate';
import { useCatalogUERConstructionTypes } from '../CreateOrEditEstimate/hooks/useCatalogConstructionTypes';

import { CatalogPriceToolbar } from './components/CatalogPriceToolbar';
import { CatalogUerBaseCostHistory } from './components/CatalogUerBaseCostHistory';
import { CatalogUerBaseCostWorkScopesModal } from './components/CatalogUerBaseCostWorkScopesModal';
import { UpdateCatalogUerBaseCostProperties } from './components/UpdateCatalogUerBaseCostProperties';
import { useCatalogBaseCostColumns } from './hooks/useCatalogBaseCostColumns';
import { useCatalogUERBaseCostEstimateBasisRows } from './hooks/useCatalogUERBaseCostEstimateBasisRows';
import { useCatalogUERBaseCostTotals } from './hooks/useCatalogUERBaseCostTotals';
import { useCatalogUERBaseData } from './hooks/useCatalogUERBaseData';
import { useRemoveUerBaseCost } from './hooks/useRemoveBaseCost';

import Styles from './catalogBaseCost.module.css';

const filters = [
    'sectionName',
    'groupName',
    'code',
    'name',
    'chapter',
    'uerClassifier',
    'workType.workCode',
    'workType.workInSum',
];

export const rowsWordForms = ['строка', 'строки', 'строк'] as ThreeForms;

export const CatalogUER = () => {
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [isModalOpen, setIsModalOpen] = useState({ isOpen: false, isEdit: false });
    const [isEstimateBasisOpen, setIsEstimateBasisOpen] = useState(false);
    const [expandedRowData, setExpandedRowData] = useState<null | CatalogUerBaseCostData>(null);
    const [expandedModalOpen, setExpandedModalOpen] = useState<boolean>(false);
    const [isHistoryOpened, setIsHistoryOpened] = useState<boolean>(false);
    const [isMassRatioApplicationModalOpen, setIsMassRatioApplicationModalOpen] = useState(false);
    const [rowData, setRowData] = useState<CatalogUerBaseCostData | null>(null);
    const context = { tableUniqueId: 'CatalogUER' };
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const { catalogPrice: catalogPriceRules } = useAccess();

    const { id } = useParams();

    const { catalogData, getCatalog } = useCatalog(id);

    const isUERBaseCostWork = !catalogData?.data?.metadata?.HasUERBaseCostWork;
    const catalogVersion = catalogData?.data?.metadata?.UERVersion;

    const { pathname } = useLocation();
    const { handleCleanEntityMemoizedValues } = usePageFilterSaver({});
    const memoWorkOrderKey = pathname.replace('/replace', '');

    const { constructionTypes } = useGlobalConstructionTypes();

    const catalogPageTitle = catalogData?.data?.name ?? '';
    const updatedAt = catalogData?.data?.updatedAt ?? '';

    const toggleHistory = useCallback(() => {
        setIsHistoryOpened((p) => !p);
    }, []);

    const toggleMassRatioApplicationModal = useCallback(() => {
        setIsMassRatioApplicationModalOpen((p) => !p);
    }, []);

    const handleExpandRow = useCallback((data: CatalogUerBaseCostData) => {
        setExpandedRowData(data);
        setExpandedModalOpen(true);
    }, []);

    const handleCloseExpandedRowModal = useCallback(() => {
        setExpandedModalOpen(false);
    }, []);

    const gridRef = useRef<AgGridReact<CatalogUerBaseCostData> | null>(null);

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, []);

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const handleOpenModalEstimate = useCallback((isEdit = false) => {
        setIsModalOpen({ isOpen: true, isEdit });
    }, []);

    const handleCloseModalEstimate = useCallback(() => {
        setIsModalOpen({ isOpen: false, isEdit: false });
        setRowData(null);
    }, []);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const handleClearFilters = useCallback(() => {
        handleCleanEntityMemoizedValues({
            pathNameKey: memoWorkOrderKey,
        });
    }, [handleCleanEntityMemoizedValues, memoWorkOrderKey]);

    const onEstimateBasisModalOpen = useCallback(() => {
        setIsEstimateBasisOpen(true);
    }, []);

    const onEstimateBasisModalClose = useCallback(() => {
        setIsEstimateBasisOpen(false);
        setRowData(null);
    }, []);

    const { catalogUERConstructionTypesItems, catalogConstructionTypesRequest } = useCatalogUERConstructionTypes(id);

    const [resetCatalogUERBaseCostUerFilter] = useResetStoreData({
        requestId: 'catalogUERBaseCostUerClassifierFilter',
    });

    const onSuccessAction = useCallback(() => {
        resetCatalogUERBaseCostUerFilter(undefined);
        getCatalog();
        refreshData();
        handleClearChecked();
        if (!isModalOpen.isEdit) {
            setFilterValues({});
            destroyAgGridTableFilters({
                api: gridRef.current?.api,
                columns: filters,
            });
            handleClearFilters();
        }
        catalogConstructionTypesRequest();
    }, [
        resetCatalogUERBaseCostUerFilter,
        getCatalog,
        refreshData,
        handleClearChecked,
        isModalOpen.isEdit,
        catalogConstructionTypesRequest,
        handleClearFilters,
    ]);

    const { fullSetColumns, handleTableSettingsConfirm, fullSetSettingsColumns, isColumnVisible } =
        useCatalogBaseCostColumns({
            checkedIds,
            handleSetCheckedIds: setCheckedIds,
            handleExpandRow,
            catalogUniqueId: id,
            isUERBaseCostWork,
            handleOpenModalEstimate,
            setRowData,
            onRemoveSuccess: onSuccessAction,
            filterValues,
            setFilterValues,
            catalogPriceRules,
            onEstimateBasisModalOpen,
        });

    const { datasource, totalCount, fetchedCount } = useCatalogUERBaseData({
        filterValues,
        id,
    });

    const { catalogRemoveBaseCostRequest } = useRemoveUerBaseCost(checkedIdsArr, onSuccessAction);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveBaseCostRequest(),
        });

    const updateCatalogUERConstructionTypesItems = useMemo(
        () => catalogUERConstructionTypesItems?.filter((i) => i.code !== ConstructionTypeCodeEnum.Unset),
        [catalogUERConstructionTypesItems],
    );

    const updateConstructionTypes = useMemo(
        () =>
            constructionTypes
                ?.filter((i) => i.code !== ConstructionTypeCodeEnum.Unset)
                ?.sort((a, b) => a.name.localeCompare(b.name)),

        [constructionTypes],
    );

    return (
        <CatalogTablePage
            catalogName={catalogPageTitle}
            methodology={ProjectMethodologyEnum.Uer}
            isError={false}
            isLoading={false}
        >
            <CatalogPriceToolbar
                catalogId={catalogData?.data?.id}
                onSuccessAction={onSuccessAction}
                updatedAt={updatedAt}
                handleOpenModalEstimate={handleOpenModalEstimate}
                handleOpenHistory={toggleHistory}
                handleOpenMassRatioApplicationModal={toggleMassRatioApplicationModal}
                catalogUERConstructionTypesItems={updateCatalogUERConstructionTypesItems}
                catalogPriceRules={catalogPriceRules}
            />

            <div className={Styles.MainWrapper}>
                <Grid cols={2} className={Styles.TableSettings}>
                    <GridItem>
                        {checkedIdsArr.length ? (
                            <Grid cols={4} yAlign="center">
                                <GridItem>
                                    <TotalCount
                                        totalCount={checkedIdsArr.length}
                                        forms={rowsWordForms}
                                        prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                    />
                                </GridItem>
                                <GridItem col={3}>
                                    <UpdateCatalogUerBaseCostProperties
                                        checkedRowIds={checkedIdsArr}
                                        catalogId={catalogData?.data?.id}
                                        refreshTable={refreshData}
                                        resetCheckedRows={handleClearChecked}
                                    />
                                    <ButtonAlert size="xs" label="Удалить" onClick={handleOpenConfirmationDialog} />
                                    <Button
                                        label="Сбросить"
                                        view="ghost"
                                        size="xs"
                                        onClick={handleClearChecked}
                                        className={cnMixSpace({ mL: 's' })}
                                    />
                                </GridItem>
                            </Grid>
                        ) : (
                            <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                        )}
                    </GridItem>
                    <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                        <TableSettings
                            columns={fullSetSettingsColumns}
                            onConfirm={handleTableSettingsConfirm}
                            isColumnVisible={isColumnVisible}
                            showPinColumn
                            tableApi={gridRef?.current}
                        />
                    </GridItem>
                </Grid>
                <div className={Styles.WrapperTable}>
                    <div className="ag-theme-alpine">
                        <AgGridTable
                            className={Styles.uerBaseCost}
                            ref={gridRef}
                            datasource={datasource}
                            columnDefs={fullSetColumns}
                            suppressCellFocus
                            rowModelType="infinite"
                            maxConcurrentDatasourceRequests={1}
                            maxBlocksInCache={50}
                            cacheBlockSize={50}
                            context={context}
                            getRowId={getRowId}
                        />
                    </div>
                </div>
            </div>

            {isHistoryOpened && <CatalogUerBaseCostHistory catalogId={id} closeHistory={toggleHistory} />}

            {expandedModalOpen && (
                <CatalogUerBaseCostWorkScopesModal
                    rowData={expandedRowData}
                    onClose={handleCloseExpandedRowModal}
                    catalogUniqueId={id}
                />
            )}

            {isModalOpen.isOpen && (
                <CreateOrEditEstimate
                    constructionTypes={updateConstructionTypes}
                    onClose={handleCloseModalEstimate}
                    isEdit={isModalOpen.isEdit}
                    uerBaseCostId={rowData?.id}
                    catalogId={catalogData.data?.id}
                    catalogUniqueId={catalogData.data?.uniqueId}
                    refreshData={onSuccessAction}
                    isUERBaseCostWork={isUERBaseCostWork}
                    catalogVersion={catalogVersion}
                    catalogName={catalogPageTitle}
                />
            )}

            {isEstimateBasisOpen && (
                <EstimateBasisModal
                    code={rowData?.code}
                    onClose={onEstimateBasisModalClose}
                    getParams={{
                        catalogUniqueId: id,
                        uerBaseCostId: rowData?.id,
                    }}
                    useUERBaseCostTotals={useCatalogUERBaseCostTotals}
                    useUERBaseCostEstimateBasisRows={useCatalogUERBaseCostEstimateBasisRows}
                />
            )}

            {isMassRatioApplicationModalOpen && <MassRatioApplication closeModal={toggleMassRatioApplicationModal} />}

            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ ЗАПИСИ"
                textConfirmation="Вы уверены,что хотите удалить запись?"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
            />
        </CatalogTablePage>
    );
};

CatalogUER.displayName = 'CatalogUER';
